import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { produce } from 'immer'
import { createPortal } from 'react-dom'
import './_newPost.scss'
import { useTranslation } from 'react-i18next'
import { BiSolidMessageRoundedCheck } from 'react-icons/bi'
import { useReactMediaRecorder } from 'react-media-recorder'
import { useQuery, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import type { CloudFrontCredentials } from 'helpers/cloudfrontCookies'
import { addToast } from 'components/Toast/Toast'
import AudioMessage from 'components/UI/AudioMessage/AudioMessage'
import AddPayoutMethodModal from 'components/UI/Modal/AddPayoutMethod/AddPayoutMethod'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import EditPreviewModal, {
  type ConfirmPayload
} from 'components/UI/Modal/EditPreviewModal/EditPreviewModal'
import SelectCategoriesModal from 'components/UI/Modal/SelectCategoriesModal/SelectCategoriesModal'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { useModalContext } from 'context/modalContext'
import { usePageTitle } from 'context/pageTitleContext'
import AddGoal from 'features/AddGoal/AddGoal'
import AddPoll from 'features/AddPoll/AddPoll'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import { AudioRecorder } from 'features/MediaRecorder/AudioRecorder'
import MediaRecorder from 'features/MediaRecorder/MediaRecorder'
import PhotoEditView from 'features/PhotoEdit/PhotoEditView'
import { fastFileHash } from 'helpers/hash/fastFileHash'
import { validateMediaFiles } from 'helpers/media'
import { insertPostIntoProfileQuery } from 'helpers/post/insertPostIntoProfileQuery'
import { randomString } from 'helpers/util'
import {
  type ClientFrame,
  type ClientThumbs,
  generateClientThumbs,
  getExpectedClientThumbCount,
  revokeClientThumbs,
  validateClientThumbs
} from 'helpers/video/generateClientThumbs'
import PostModal from 'pages/postModal'
import {
  abortVideoMultipart,
  attachAudio,
  attachPhotoFromS3,
  attachPreviewCoverFromS3,
  attachVideoFromS3,
  checkPhotoDupe,
  checkVideoDupe,
  completeVideoMultipart,
  getVideoThumbs,
  presignPhoto,
  startVideoMultipart,
  uploadFileToS3,
  uploadVideoMultipart
} from 'services/endpoints/attachment'
import { uploadGoal, uploadPoll, uploadPost } from 'services/endpoints/post'
import { getProfileGroups } from 'services/endpoints/social'
import { useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import {
  type ICategory,
  type IGoal,
  type IGroup,
  type INewPost,
  type IPoll,
  type IProgressInfo,
  type ITag
} from 'types/interfaces/ITypes'

import { AddAnything } from './components/AddAnything'
import { useSortableProgress } from './components/Drag'
import { GoalPreview } from './components/GoalPreview/GoalPreview'
import { PollPreview } from './components/PollPreview'
import { PostImage } from './components/PostImage'
import { PostText } from './components/PostText'
import { PostVideo } from './components/PostVideo'
import { PostVisibility } from './components/PostVisibility'
import { PostInputs } from './features/PostInputs'
import { PostMediaInputs } from './features/PostMediaInputs'
import { useAudioRecorder } from '../../helpers/hooks/useAudioRecorder/useAudioRecorder'

type VideoPreviewData = {
  defaultPreview: string
  storageSrc: string
  videoId: string
  duration?: number
  orientation?: string
  thumbnails?: string[]
  thumbnailTimes?: number[]
  scrubFrames?: ClientFrame[]
  storyboard?: {
    manifest: string
    base_url: string
    thumbnail_width: number
    thumbnail_height: number
    tile_width: number
    tile_height: number
    interval: number
    sheet_count: number
    frame_count: number
  } | null
  cloudfront?: CloudFrontCredentials | null
  customPreviewCloudfront?: CloudFrontCredentials | null
  thumbnailIndex?: number
  previewThumbnailIndex?: number | null
  customPreviewThumbKey?: string | null
  customPreviewThumbnailUrl?: string | null
  thumbsQueryDone?: boolean
  trimPreview?: { start: number; end: number }
  thumbType?: 'clip' | 'thumb'
  customThumbnailUrl?: string | null
  customThumbnailTime?: number | null
  customPreviewUrl?: string | null
  previewVideoId?: string | null
}
type UploadStage = 'idle' | 'hash' | 'dupe' | 'presign' | 'upload' | 'attach' | 'done'

export const cid = () => Math.random().toString(36).slice(2)

export const NewPost = () => {
  const { setTitle } = usePageTitle()
  const { setHandleGoBack } = useHeaderLayout()
  const modalContext = useModalContext()
  const modalData = useModalContext()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const userData = useUserProfile()!
  const { t } = useTranslation()

  const [pollData, setPollData] = useState<IPoll | null>(null)
  const [goalData, setGoalData] = useState<IGoal | null>(null)
  const [post, setPost] = useState<INewPost>({
    body: '',
    tags: [],
    groups: [],
    categories: [],
    price: null,
    schedule_date: null,
    shareOnTwitter: false,
    photos: [],
    sounds: [],
    videos: []
  })

  const defaultGroup = userData?.default_fan_group
  const [postAudience, setPostAudience] = useState<IGroup[]>(defaultGroup ? [defaultGroup] : [])
  const [selectedCategories, setSelectedCateories] = useState<ICategory[]>([])
  const [postType, setPostType] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [progressInfos, setProgressInfos] = useState<{ val: IProgressInfo[] }>({ val: [] })
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewMedia, setPreviewMedia] = useState<any>(null)
  const [hasAudio] = useState(false)
  const [audioOnly, setAudioOnly] = useState(false)
  const [postUploading, setPostUploading] = useState(false)
  const [recorderType, setRecorderType] = useState('')
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([])
  const [addPollOpen, setAddPollOpen] = useState(false)
  const [addGoalOpen, setAddGoalOpen] = useState(false)
  const [twitterInitShare, setTwitterInitShare] = useState(false)
  const [editPreviewModalOpen, setEditPreviewModalOpen] = useState(false)
  const [videoPreviewData, setVideoPreviewData] = useState<VideoPreviewData | null>(null)
  const [selectedTaggedCreators, setSelectedTaggedCreators] = useState<any[]>([])
  const [tagCreatorsOpen, setTagCreatorsOpen] = useState(false)
  const [previewPost, setPreviewPost] = useState<any>(null)

  const incrementProfileMediaCounts = useProfileCacheStore(state => state.incrementMediaCounts)

  const progressInfosRef = useRef<any>(null)
  const postAudienceRef = useRef<IGroup[]>(postAudience)
  const priceRef = useRef<number | null>(post.price)
  const progressNextRef = useRef<IProgressInfo[] | null>(null)
  const syncRafRef = useRef<number | null>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const lastSortedIdsRef = useRef<string[]>([])

  const uploadOpsRef = useRef<
    Record<
      string,
      {
        aborted: boolean
        controller: AbortController
        stage: UploadStage
        cancelCurrent?: () => void
      }
    >
  >({})

  const PreviouslyMessaged = BiSolidMessageRoundedCheck as React.ComponentType<{
    color?: string
    width?: string
    height?: string
    style?: React.CSSProperties
  }>

  const attachmentCount = progressInfos.val.filter(
    item => item.type === 'photo' || item.type === 'video'
  ).length

  const { data: groups = [] } = useQuery('allGroups', getProfileGroups, {
    refetchOnWindowFocus: false,
    staleTime: 5000
  })

  const {
    startRecording: startAudioRecording,
    stopRecording: stopAudioRecording,
    audioFile,
    recording: audioRecording
  } = useAudioRecorder()

  const { status, startRecording, stopRecording } = useReactMediaRecorder({
    audio: true,
    video: recorderType === 'video'
  })

  useEffect(() => {
    postAudienceRef.current = postAudience
  }, [postAudience])

  useEffect(() => {
    priceRef.current = post.price
  }, [post.price])

  const normalizeEntityType = (value?: string | null) => (value ?? '').split('\\').pop() ?? ''

  const isMessageEntity = (value?: string | null) =>
    ['Message', 'MassMessage'].includes(normalizeEntityType(value))

  const extractGroupIds = (groups: IGroup[]) => groups.map(group => group.id)
  const extractCategoryIds = (categories: ICategory[]) => categories.map(category => category.id)

  const firstPhotoSeed = useMemo(() => {
    const index = progressInfos.val.findIndex(item => item.type === 'photo')
    if (index < 0) return null

    const media = progressInfos.val[index]
    const maybeFile = selectedFiles[index]

    return {
      previewUrl: media.previewUrl ?? null,
      storageUrl: media.storageUrl ?? null,
      orientation: media.orientation ?? '',
      file: maybeFile instanceof File ? maybeFile : null
    }
  }, [progressInfos.val, selectedFiles])

  const hasOnlyPhotos =
    progressInfos.val.length > 0 && progressInfos.val.every(item => item.type === 'photo')

  const hasBlockingMediaForPollGoal = progressInfos.val.some(item =>
    ['video', 'audio', 'sound'].includes(item.type)
  )

  const ensureUploadOp = (clientId: string) => {
    if (!uploadOpsRef.current[clientId]) {
      uploadOpsRef.current[clientId] = {
        aborted: false,
        controller: new AbortController(),
        stage: 'idle'
      }
    }

    return uploadOpsRef.current[clientId]
  }

  const setUploadStage = (clientId: string, stage: UploadStage) => {
    ensureUploadOp(clientId).stage = stage
  }

  const abortUploadOp = (clientId: string) => {
    const op = uploadOpsRef.current[clientId]
    if (!op) return

    op.aborted = true

    try {
      op.cancelCurrent?.()
    } catch {}

    try {
      op.controller.abort()
    } catch {}
  }

  const clearUploadOp = (clientId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete uploadOpsRef.current[clientId]
  }

  const isUploadAborted = (clientId: string) => !!uploadOpsRef.current[clientId]?.aborted

  const throwIfUploadAborted = (clientId: string) => {
    if (!isUploadAborted(clientId)) return

    const error = new Error('__UPLOAD_ABORTED__')
    ;(error as any).code = '__UPLOAD_ABORTED__'
    throw error
  }

  const applyDuplicateVideoData = (
    entry: any,
    data: any,
    fallback?: { duration?: number; orientation?: string; hash?: string }
  ) => {
    const d = data ?? {}
    const backendDuration =
      d.duration != null && Number.isFinite(Number(d.duration)) ? Number(d.duration) : null

    entry.type = 'video'
    entry.processing = false
    entry.uploading = false
    entry.percentage = 100
    entry.error = ''
    entry.locked = !!priceRef.current
    entry.storageUrl = d.storage_path ?? d.path ?? entry.storageUrl ?? null
    entry.video_id = d.video_id ?? d.id ?? entry.video_id ?? null
    entry.duration =
      backendDuration != null && backendDuration > 0
        ? backendDuration
        : fallback?.duration && fallback.duration > 0
          ? fallback.duration
          : entry.duration ?? null
    entry.orientation =
      d.original_orientation ?? d.orientation ?? fallback?.orientation ?? entry.orientation
    entry.hash_video = d.video_hash ?? fallback?.hash ?? entry.hash_video ?? null
    entry.exif_video_media = d.exif_media ?? null
    entry.is_duplicate = true

    if (
      Array.isArray(d.thumbnails) &&
      d.thumbnails.length > 0
    ) {
      entry.thumbnails = d.thumbnails
      entry.thumbTarget = d.thumbnails.length
      entry.thumbsDone = true
      entry.thumbsQueryDone = true
      entry.thumbs_from = 'server'
    }

    if (Array.isArray(d.thumbnail_times)) {
      entry.thumbnailTimes = d.thumbnail_times
    } else if (Array.isArray(d.thumbnailTimes)) {
      entry.thumbnailTimes = d.thumbnailTimes
    }

    const customThumbnailUrl =
      typeof d.custom_thumbnail === 'string' && d.custom_thumbnail.trim()
        ? d.custom_thumbnail
        : null

    const customThumbnailTime =
      d.custom_thumbnail_time != null && Number.isFinite(Number(d.custom_thumbnail_time))
        ? Number(d.custom_thumbnail_time)
        : null

    if (customThumbnailUrl && customThumbnailTime !== null) {
      entry.imagePreview = customThumbnailUrl
      entry.customThumbnailUrl = customThumbnailUrl
      entry.custom_thumbnail = customThumbnailTime
      entry.activeThumbnailIndex = null
    } else {
      if (typeof d.active_thumb === 'string') entry.imagePreview = d.active_thumb

      entry.customThumbnailUrl = null
      entry.custom_thumbnail = null

      if (entry.imagePreview && Array.isArray(entry.thumbnails)) {
        const index = entry.thumbnails.indexOf(entry.imagePreview)
        if (index >= 0) entry.activeThumbnailIndex = index
      }
    }
  }

  const syncProgress = (incoming: IProgressInfo[]) => {
    let next = incoming

    if (lastSortedIdsRef.current.length) {
      const byId = new Map(next.map(item => [getId(item), item]))
      const ordered = lastSortedIdsRef.current
        .map(id => byId.get(id))
        .filter(Boolean) as IProgressInfo[]

      for (const item of next) {
        if (!ordered.includes(item)) ordered.push(item)
      }

      next = ordered
    }

    progressNextRef.current = next
    if (syncRafRef.current != null) return

    syncRafRef.current = requestAnimationFrame(() => {
      syncRafRef.current = null

      const finalNext = progressNextRef.current ?? []
      progressNextRef.current = null

      setProgressInfos({ val: finalNext })
      progressInfosRef.current = { val: finalNext }
    })
  }

  const { getId } = useSortableProgress({
    dropRef,
    itemCount: progressInfos.val.length,
    onSort: ids => {
      lastSortedIdsRef.current = ids

      setProgressInfos(prev => {
        const byId = new Map(prev.val.map(item => [getId(item), item]))
        const next = ids.map(id => byId.get(id)).filter((item): item is IProgressInfo => Boolean(item))

        if (next.length !== prev.val.length) {
          const seen = new Set(next.map(getId))
          for (const item of prev.val) if (!seen.has(getId(item))) next.push(item)
        }

        if (post.price) {
          const firstUnlockedIdx = next.findIndex(item => !item.locked)

          if (firstUnlockedIdx > 0) {
            const [unlocked] = next.splice(firstUnlockedIdx, 1)
            next.unshift(unlocked)
          }
        }

        progressInfosRef.current = { val: next }
        return { val: next }
      })

      setSelectedFiles(prevFiles => {
        const pairs = progressInfos.val
          .map((info, index) => [getId(info), prevFiles[index]] as const)
          .filter(([, file]) => !!file)

        const byId = new Map<string, File>(pairs)
        const nextFiles = ids.map(id => byId.get(id)).filter((file): file is File => !!file)

        if (nextFiles.length !== prevFiles.length) {
          const seen = new Set(nextFiles)
          for (const file of prevFiles) if (!seen.has(file)) nextFiles.push(file)
        }

        if (post.price) {
          queueMicrotask(() => {
            setSelectedFiles(currentFiles => {
              const idToFile = new Map<string, File>()

              for (let i = 0; i < progressInfos.val.length; i++) {
                const id = getId(progressInfos.val[i])
                const file = nextFiles[i] ?? currentFiles[i]
                if (file) idToFile.set(id, file)
              }

              const normalizedFiles: File[] = []

              for (const info of progressInfos.val) {
                const file = idToFile.get(getId(info))
                if (file) normalizedFiles.push(file)
              }

              return normalizedFiles.length ? normalizedFiles : currentFiles
            })
          })
        }

        return nextFiles
      })
    }
  })

  const safeSyncById = (
    clientId: string,
    updater: (entry: any) => void,
    fallback?: (list: any[]) => void
  ) => {
    if (isUploadAborted(clientId)) return

    const list = [...(progressInfosRef.current?.val ?? [])]
    const index = list.findIndex(item => item.id === clientId)

    if (index >= 0) {
      updater(list[index])
      syncProgress(list)
    } else {
      fallback?.(list)
    }
  }

  const revokeClientThumbAssets = (entry: any) => {
    const thumbs: ClientFrame[] = Array.isArray(entry?.thumbnails)
      ? entry.thumbnails
          .map((url: unknown, index: number) => ({
            url: typeof url === 'string' ? url : '',
            time: Number(entry?.thumbnailTimes?.[index] ?? 0)
          }))
          .filter((frame: ClientFrame) => frame.url.startsWith('blob:'))
      : []

    const scrubFrames: ClientFrame[] = Array.isArray(entry?.scrubFrames)
      ? entry.scrubFrames.filter(
          (frame: ClientFrame) => typeof frame?.url === 'string' && frame.url.startsWith('blob:')
        )
      : []

    revokeClientThumbs({ thumbs, scrubFrames })
  }

  const applyGeneratedClientThumbs = (entry: any, generated: ClientThumbs) => {
    const thumbnails = generated.thumbs.map(frame => frame.url)
    const thumbnailTimes = generated.thumbs.map(frame => frame.time)
    const currentIndex =
      typeof entry.activeThumbnailIndex === 'number' &&
      entry.activeThumbnailIndex >= 0 &&
      entry.activeThumbnailIndex < thumbnails.length
        ? entry.activeThumbnailIndex
        : 0

    entry.thumbnails = thumbnails
    entry.thumbnailTimes = thumbnailTimes
    entry.scrubFrames = generated.scrubFrames
    entry.thumbTarget = thumbnails.length
    entry.thumbsDone = thumbnails.length > 0
    entry.thumbsQueryDone = thumbnails.length > 0
    entry.thumbs_from = 'client'

    if (!entry.customThumbnailUrl && entry.custom_thumbnail == null && thumbnails.length) {
      entry.activeThumbnailIndex = currentIndex
      entry.imagePreview = thumbnails[currentIndex] ?? thumbnails[0] ?? entry.imagePreview ?? null
    }
  }

  const rerunDupeChecks = async (groups: IGroup[]) => {
    const group_ids = extractGroupIds(groups)
    const current = [...(progressInfosRef.current?.val ?? [])]

    const next = await Promise.all(
      current.map(async item => {
        try {
          if (item.type === 'photo' && item.hash_image) {
            const dupe = await checkPhotoDupe({
              h: item.hash_image,
              size: item.size ?? 1,
              group_ids
            })

            return {
              ...item,
              exif_media: dupe?.status === 'OK' ? dupe.data?.exif_media ?? null : null
            }
          }

          if (item.type === 'video' && item.hash_video) {
            const dupe = await checkVideoDupe({
              h: item.hash_video,
              size: item.size ?? 1,
              group_ids
            })

            if (dupe?.status !== 'OK') return { ...item, exif_video_media: null }

            const updatedItem: any = { ...item }

            applyDuplicateVideoData(updatedItem, dupe.data, {
              duration: item.duration,
              orientation: item.orientation,
              hash: item.hash_video
            })

            return updatedItem
          }
        } catch (error) {
          console.error('Failed to rerun media duplicate check', error)
        }

        return item
      })
    )

    syncProgress(next)
  }

  const appendAttachments = useCallback(
    async (items: any[]) => {
      if (!items?.length) return

      const current = (progressInfosRef.current?.val ?? progressInfos.val) as any[]

      const initialItems = items.map((item: any) => {
        if (item?.type === 'photo') {
          return {
            ...item,
            photo_id: item.photo_id ?? item.id
          }
        }

        if (
          item?.type === 'video' &&
          typeof item.imagePreview === 'string' &&
          item.imagePreview.length
        ) {
          return {
            ...item,
            video_id: item.video_id ?? item.id,
            thumbnails: [item.imagePreview],
            activeThumbnailIndex: 0,
            thumbsQueryDone: false
          }
        }

        return item
      })

      const appendedItems = [...current, ...initialItems]

      syncProgress(appendedItems)
      setSelectedFiles(previous => [...previous, ...initialItems])

      const videoIds = Array.from(
        new Set(
          items
            .filter((item: any) => item?.type === 'video')
            .map((item: any) => item.video_id ?? item.id)
            .filter(
              (videoId): videoId is string => typeof videoId === 'string' && videoId.length > 0
            )
        )
      )

      if (!videoIds.length) return

      try {
        const response = await getVideoThumbs(videoIds)
        const rows = response?.data?.data ?? response?.data ?? []

        if (!Array.isArray(rows)) return

        const videoThumbDataById = new Map<
          string,
          {
            thumbs: {
              src: string
              active?: boolean
              time?: number | null
              type?: string
            }[]
            storyboard?: any
          }
        >()

        rows.forEach((row: any) => {
          const videoId = row?.id ?? row?.video_id

          if (
            typeof videoId !== 'string' ||
            !videoId.length ||
            !Array.isArray(row?.thumbs)
          ) {
            return
          }

          videoThumbDataById.set(videoId, {
            thumbs: row.thumbs,
            storyboard: row.storyboard ?? null
          })
        })

        const latestItems = (progressInfosRef.current?.val ?? appendedItems) as any[]

        const hydratedItems = latestItems.map(item => {
          if (item?.type !== 'video') return item

          const videoId = item.video_id ?? item.id
          if (typeof videoId !== 'string' || !videoId.length) return item

          const videoThumbData = videoThumbDataById.get(videoId)

          if (!videoThumbData?.thumbs?.length) return item

          const allThumbRows = videoThumbData.thumbs

          const generatedThumbRows = allThumbRows.filter(
            thumb => !thumb?.type || thumb.type === 'thumb'
          )

          const thumbnails = generatedThumbRows
            .map(thumb => thumb?.src)
            .filter((src): src is string => typeof src === 'string' && src.length > 0)

          const thumbnailTimes = generatedThumbRows.map(thumb =>
            thumb?.time != null && Number.isFinite(Number(thumb.time))
              ? Number(thumb.time)
              : null
          )

          const activeThumbnailIndex = generatedThumbRows.findIndex(thumb => thumb.active === true)
          const resolvedActiveIndex = activeThumbnailIndex >= 0 ? activeThumbnailIndex : 0

          return {
            ...item,
            video_id: videoId,
            thumbnails,
            thumbnailTimes,
            storyboard: videoThumbData.storyboard ?? null,
            activeThumbnailIndex: item.customThumbnailUrl ? null : resolvedActiveIndex,
            imagePreview:
              item.imagePreview ??
              thumbnails[resolvedActiveIndex] ??
              thumbnails[0] ??
              null,
            thumbsQueryDone: true,
            thumbsDone: true,
            thumbTarget: thumbnails.length,
            thumbs_from: 'server'
          }
        })

        syncProgress(hydratedItems)

        setVideoPreviewData(previous => {
          if (!previous) return previous

          const hydratedVideo = hydratedItems.find(
            item => item?.type === 'video' && item.previewUrl === previous.defaultPreview
          )

          if (!hydratedVideo) return previous

          return {
            ...previous,
            thumbnails: hydratedVideo.thumbnails,
            thumbnailTimes: hydratedVideo.thumbnailTimes,
            thumbnailIndex:
              typeof hydratedVideo.activeThumbnailIndex === 'number'
                ? hydratedVideo.activeThumbnailIndex
                : previous.thumbnailIndex,
            thumbsQueryDone: true
          }
        })

        setSelectedFiles(hydratedItems)
      } catch (error) {
        console.error('[NewPost] Failed to load Vault video thumbnails', error)
      }
    },
    [progressInfos.val]
  )

  useEffect(() => {
    return () => {
      if (syncRafRef.current != null) cancelAnimationFrame(syncRafRef.current)
    }
  }, [])

  const confirmationMessage = t('videoUploadInProgressVideoWillNotBeSaved')

  const beforeUnloadHandler = (event: any) => {
    ;(event || window.event).returnValue = confirmationMessage
    return confirmationMessage
  }

  useEffect(() => {
    setTitle(editPreviewModalOpen ? 'Edit Preview' : 'New Post')
  }, [editPreviewModalOpen, setTitle])

  useEffect(() => {
    const backHandler = () => {
      if (editPreviewModalOpen) setEditPreviewModalOpen(false)
      else navigate(-1)
    }

    setHandleGoBack(() => backHandler)
    return () => setHandleGoBack(undefined)
  }, [editPreviewModalOpen, navigate, setHandleGoBack])

  useEffect(() => {
    if (!twitterInitShare && userData?.auto_post_to_twitter) {
      setPost(prev => ({ ...prev, shareOnTwitter: userData.auto_post_to_twitter }))
      setTwitterInitShare(true)
    }
  }, [twitterInitShare, userData?.auto_post_to_twitter])

  useEffect(() => {
    const hasMedia = progressInfosRef.current?.val?.length > 0

    if (hasMedia) window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler)
  }, [])

  const postReady = useMemo(() => {
    const hasValidText = !!post.body?.trim()

    const isPhotoFinished = (item: IProgressInfo) =>
      item.type === 'photo' &&
      typeof item.photo_id === 'string' &&
      item.photo_id.length > 0 &&
      !item.processing &&
      !item.uploading &&
      !item.error

    const isVideoFinished = (item: IProgressInfo) =>
      item.type === 'video' &&
      (!!item.video_id || !!item.storageUrl) &&
      !item.processing &&
      !item.error

    const isPV = (item: IProgressInfo) => item.type === 'photo' || item.type === 'video'
    const isPVFinished = (item: IProgressInfo) =>
      item.type === 'photo' ? isPhotoFinished(item) : isVideoFinished(item)

    const photoVideos = progressInfos.val.filter(isPV)
    const hasAnyPV = photoVideos.length > 0
    const allPVFinished = photoVideos.every(isPVFinished)
    const hasAnyAttachment = progressInfos.val.length > 0

    if (!hasValidText && !hasAnyAttachment && !pollData && !goalData) return false
    if (hasAnyPV && !allPVFinished) return false
    if (post.price && !photoVideos.some(isPVFinished)) return false

    const hasAnyFinishedAttachment = progressInfos.val.some(item => {
      if (isPV(item)) return isPVFinished(item)
      if (item.type === 'audio') return !!item.storageUrl && !item.error
      return false
    })

    return hasValidText || hasAnyFinishedAttachment || !!pollData || !!goalData
  }, [post.body, post.price, progressInfos.val, pollData, goalData])

  useEffect(() => {
    const isAudioOnly =
      progressInfos.val.length === 1 &&
      ['sound', 'audio'].includes(progressInfos.val[0].type)

    setAudioOnly(isAudioOnly)
  }, [progressInfos.val])

  useEffect(() => {
    if (!audioFile) return

    setSelectedFiles(prev => [...prev, audioFile])
    uploadFiles([audioFile])
    setPostType('media')
    setRecordedChunks([])
  }, [audioFile])

  useEffect(() => {
    if (!recordedChunks.length) return

    const blob = recordedChunks[0]
    const file = new File([blob], randomString(blob.type), { type: blob.type })

    setSelectedFiles(prev => [...prev, file])
    uploadFiles([file])
    setPostType('media')
    setRecordedChunks([])
  }, [recordedChunks])

  useEffect(() => {
    if (!location.search) return

    const dateString = location.search.split('?date=')[1]
    if (dateString) {
      setPost(prev => ({ ...prev, schedule_date: new Date(dateString) }))
    }
  }, [])

  const getPreviewStatus = (status: string) => status !== 'no-preview'

  const handleOpenCategories = () => {
    modalData.addModal(
      t('categories'),
      <SelectCategoriesModal
        selectedCategories={selectedCategories}
        onCancel={() => modalData.clearModal()}
        hideAllCategory
        onSave={selected => {
          setSelectedCateories(selected)
          modalData.clearModal()
        }}
      />,
      false,
      true,
      'selectCategories'
    )
  }

  const revokeIfBlob = (url?: string) => {
    if (!url?.startsWith('blob:')) return

    try {
      URL.revokeObjectURL(url)
    } catch {}
  }

  const removeFile = (index: number) => {
    const item = progressInfos.val[index]
    if (!item) return

    abortUploadOp(String(item.id))
    revokeIfBlob(item.previewUrl)
    revokeClientThumbAssets(item)

    const nextProgressInfos = progressInfos.val.filter((_, i) => i !== index)
    const nextSelectedFiles = selectedFiles.filter((_, i) => i !== index)

    setProgressInfos({ val: nextProgressInfos })
    progressInfosRef.current = { val: nextProgressInfos }
    setSelectedFiles(nextSelectedFiles)
  }

  useEffect(() => {
    return () => {
      const list = progressInfosRef.current?.val ?? []
      for (const item of list) {
        revokeIfBlob(item.previewUrl)
        revokeClientThumbAssets(item)
      }
    }
  }, [])

  const selectFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const tmpFiles = event.target.files
    if (!tmpFiles?.length) return

    const files = await validateMediaFiles(Array.from(tmpFiles), t)
    const audioFiles = files.filter(file => file.type.includes('audio/'))
    const prevAudioFiles = progressInfos.val.filter(
      info => info.type === 'sound' || info.type === 'audio'
    )

    const videoPhotoFiles = files.filter(
      file => file.type.includes('video/') || file.type.includes('image/')
    )

    const tmpVideoPhotoFiles = videoPhotoFiles.filter(file => {
      const unique = !selectedFiles.some(selected => selected.size === file.size)

      if (!unique) addToast('error', t('error:cannotUploadDuplicateFile'))
      return unique
    })

    if (
      audioFiles.length > 1 ||
      (audioFiles.length >= 1 && prevAudioFiles.length === 1)
    ) {
      addToast('error', t('youCanUploadOnlyOneAudioFilePerPost'))
    }

    if (files.length + progressInfos.val.length > 40) {
      addToast('error', t('maximumNumberOfMediaFilesExceeded'))
      return
    }

    if (audioFiles.length > 0) {
      if (prevAudioFiles.length === 1) {
        uploadFiles(tmpVideoPhotoFiles)
        setSelectedFiles(prev => [...prev, ...tmpVideoPhotoFiles])
      } else {
        const uploadSelection = [...tmpVideoPhotoFiles, audioFiles[0]]
        uploadFiles(uploadSelection)
        setSelectedFiles(prev => [...prev, ...uploadSelection])
      }
    } else {
      uploadFiles(tmpVideoPhotoFiles)
      setSelectedFiles(prev => [...prev, ...tmpVideoPhotoFiles])
    }

    setPostType('media')
  }

  useEffect(() => {
    if (!localStorage.getItem('share-post')) return

    ;(async () => {
      const sharedPostArray = localStorage.getItem('share-post')
      const parsedArray = sharedPostArray && JSON.parse(sharedPostArray)

      const nextProgressInfos = progressInfosRef.current
        ? [...progressInfosRef.current.val]
        : []

      const idsArray = parsedArray.map((media: any) => {
        if (media.post?.videos?.length) return media.post.videos.map((video: any) => video.id)
        if (media.message?.message?.videos?.length) {
          return media.message.message.videos.map((video: any) => video.id)
        }
        if (media.story?.video_id) return media.story.video_id
        return null
      })

      const idsArrayConcated = idsArray.concat.apply([], idsArray).filter(Boolean)
      const thumbsData = idsArrayConcated.length ? await getVideoThumbs(idsArrayConcated) : null

      const getThumbData = (videoId: string) => {
        const row = thumbsData?.data?.data?.find((thumb: any) => thumb.id === videoId)
        const onlyThumbs = row?.thumbs?.filter((thumb: any) => thumb.type === 'thumb') ?? []
        const active = onlyThumbs.find((thumb: any) => thumb.active)

        return {
          thumbnails: onlyThumbs.map((thumb: any) => thumb.src),
          thumbnailIds: onlyThumbs.map((thumb: any) => thumb.id),
          thumbnailTimes: onlyThumbs.map((thumb: any) =>
            thumb.time != null ? Number(thumb.time) : null
          ),
          activeImage: active?.src,
          activeThumbId: active?.id ?? null,
          activeThumbnailIndex: active ? onlyThumbs.indexOf(active) : 0,
          storyboard: row?.storyboard ?? null,
        }
      }

      for (const media of parsedArray) {
        if (media.post) {
          for (const photo of media.post.photos ?? []) {
            nextProgressInfos.push({
              id: photo.id,
              cancel: () => {},
              error: '',
              fileName: `shared-photo-${photo.id}`,
              locked: false,
              orientation: photo.orientation,
              percentage: 100,
              previewUrl: photo.url,
              storageUrl: photo.url,
              type: 'photo',
              text: photo.text
            })
          }

          for (const video of media.post.videos ?? []) {
            const thumbData = getThumbData(video.id)

            nextProgressInfos.push({
              id: video.id,
              video_id: video.id,
              cancel: () => {},
              error: '',
              fileName: `shared-video-${video.id}`,
              locked: false,
              orientation: video.orientation,
              duration: video.duration,
              percentage: 100,
              imagePreview: thumbData.activeImage,
              previewUrl: video.url,
              storageUrl: video.url,
              cloudfront: video.cloudfront ?? null,
              type: 'video',
              previewOn: false,
              thumbnails: thumbData.thumbnails,
              thumbnailIds: thumbData.thumbnailIds,
              thumbnailTimes: thumbData.thumbnailTimes,
              storyboard: thumbData.storyboard,
              active_thumbnail: thumbData.activeThumbId,
              activeThumbnailIndex: thumbData.activeThumbnailIndex,
              thumbTarget: thumbData.thumbnails.length,
              thumbsDone: true,
              thumbsQueryDone: true,
              thumbs_from: 'server'
            } as any)
          }
        }

        if (media.story) {
          const story = media.story

          if (story.image_src) {
            nextProgressInfos.push({
              id: story.id,
              cancel: () => {},
              error: '',
              fileName: `shared-story-photo-${story.id}`,
              locked: false,
              orientation: story.image_orientation,
              percentage: 100,
              previewUrl: story.image_src,
              storageUrl: story.image_src,
              type: 'photo',
              text: story.text
            })
          }

          if (story.video_src) {
            const thumbData = story.video_id
              ? getThumbData(story.video_id)
              : {
                  thumbnails: [],
                  thumbnailIds: [],
                  thumbnailTimes: [],
                  activeImage: undefined,
                  activeThumbId: null,
                  activeThumbnailIndex: 0
                }

            nextProgressInfos.push({
              id: story.id,
              video_id: story.video_id,
              cancel: () => {},
              error: '',
              fileName: `shared-story-video-${story.id}`,
              locked: false,
              orientation: story.video_orientation,
              percentage: 100,
              imagePreview: thumbData.activeImage,
              previewUrl: story.video_src,
              storageUrl: story.video_src,
              cloudfront: story.video_cloudfront ?? null,
              type: 'video',
              thumbnails: thumbData.thumbnails,
              thumbnailTimes: thumbData.thumbnailTimes,
              activeThumbnailIndex: thumbData.activeThumbnailIndex,
              thumbTarget: thumbData.thumbnails.length,
              thumbsDone: true,
              thumbsQueryDone: true,
              thumbs_from: 'server',
              previewOn: false
            } as any)
          }
        }

        if (media.message?.message) {
          for (const photo of media.message.message.photos ?? []) {
            nextProgressInfos.push({
              id: photo.id,
              cancel: () => {},
              error: '',
              fileName: `shared-photo-${photo.id}`,
              locked: false,
              orientation: photo.orientation,
              percentage: 100,
              previewUrl: photo.url,
              storageUrl: photo.url,
              type: 'photo',
              text: photo.text
            })
          }

          for (const video of media.message.message.videos ?? []) {
            const thumbData = getThumbData(video.id)

            nextProgressInfos.push({
              id: video.id,
              video_id: video.id,
              cancel: () => {},
              error: '',
              fileName: `shared-video-${video.id}`,
              locked: false,
              type: 'video',
              percentage: 100,
              previewOn: false,
              orientation: video.orientation,
              imagePreview: thumbData.activeImage,
              previewUrl: video.url,
              storageUrl: video.url,
              cloudfront: video.cloudfront ?? null,
              thumbnails: thumbData.thumbnails,
              thumbnailTimes: thumbData.thumbnailTimes,
              storyboard: thumbData.storyboard,
              activeThumbnailIndex: thumbData.activeThumbnailIndex,
              thumbTarget: thumbData.thumbnails.length,
              thumbsDone: true,
              thumbsQueryDone: true,
              thumbs_from: 'server'
            } as any)
          }
        }
      }

      setProgressInfos({ val: nextProgressInfos })
      progressInfosRef.current = { val: nextProgressInfos }
    })()

    localStorage.removeItem('share-post')
  }, [])

  const uploadCustomVideoPreview = async (
    file: File,
    signal?: AbortSignal
  ): Promise<string> => {
    const meta = await import('helpers/video/readClientMeta')
      .then(async ({ readClientVideoMeta }) => await readClientVideoMeta(file))
      .catch(() => null)

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    const duration = meta?.duration || 0
    const orientation = meta?.orientation

    const multipart = await startVideoMultipart({
      type: 'post_video',
      contentType: file.type,
      size: file.size
    })

    const { key, uploadId, partSize, totalParts, parts } = multipart

    try {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }

      const completedParts = await uploadVideoMultipart({
        file,
        key,
        uploadId,
        partSize,
        totalParts,
        parts,
        contentType: file.type,
        concurrency: 4,
        setProgress: () => {}
      })

      if (!completedParts?.length) {
        throw new Error('Preview video upload did not complete.')
      }

      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }

      await completeVideoMultipart({
        key,
        uploadId,
        parts: completedParts
      })

      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }

      const response = await attachVideoFromS3({
        s3_key: key,
        duration: duration > 0
          ? Number(duration.toFixed(2))
          : null,
        orientation: orientation ?? null,
        preview: true,
        is_preview_video: true,
        type: 'post'
      } as any)

      const data =
        response?.data?.data ??
        response?.data ??
        response

      if (typeof data?.id !== 'string' || !data.id) {
        throw new Error('Preview video did not attach.')
      }

      return data.id
    } catch (error) {
      try {
        await abortVideoMultipart({ key, uploadId })
      } catch {}

      throw error
    }
  }
const uploadCustomPreviewThumbnail = async (
  file: File,
  videoId: string
): Promise<string> => {
  const { url: putUrl, key } = await presignPhoto({
    type: 'preview_cover',
    contentType: file.type,
    size: file.size
  })

  await uploadFileToS3(
    putUrl,
    file,
    () => {}
  )

  const response = await attachPreviewCoverFromS3({
    video_id: videoId,
    s3_key: key,
    expected_bytes: file.size
  })

  const data =
    response?.data?.data ??
    response?.data ??
    response

  if (typeof data?.key !== 'string' || !data.key) {
    throw new Error(
      'Preview cover did not attach.'
    )
  }

  return data.key
}

  const uploadCustomVideoThumbnail = async (file: File): Promise<string> => {
    const { url: putUrl, key } = await presignPhoto({
      type: 'post_image',
      contentType: file.type,
      size: file.size
    })

    await uploadFileToS3(putUrl, file, () => {})
    return key
  }

const upload = useCallback(
  async (file: File, clientId: string) => {
    let currentProgress = [...(progressInfosRef.current?.val ?? [])]

    const data = new FormData()
    const op = ensureUploadOp(clientId)
    const signal = op.controller.signal

    try {
      if (file.type.includes('image')) {
        setUploadStage(clientId, 'hash')

        const fileHash = await fastFileHash(file)
        throwIfUploadAborted(clientId)

        setUploadStage(clientId, 'dupe')

        const group_ids = extractGroupIds(postAudienceRef.current)
        const dupe = await checkPhotoDupe(
          { h: fileHash, size: file.size, group_ids },
          signal
        )

        throwIfUploadAborted(clientId)

        if (dupe?.status === 'OK') {
          safeSyncById(clientId, entry => {
            entry.photo_id = dupe.data.id
            entry.storageUrl = dupe.data.path ?? dupe.data.storage_path
            entry.status = 'finished'
            entry.exif_media = dupe.data.exif_media ?? null
            entry.orientation = dupe.data.original_orientation ?? entry.orientation
            entry.processing = false
            entry.uploading = false
            entry.percentage = 100
            entry.locked = !!priceRef.current
            entry.hash_image = dupe.data.image_hash ?? fileHash
            entry.is_duplicate = true
          })

          setPreviewPost(dupe.data.post ?? null)
          setUploadStage(clientId, 'done')
          clearUploadOp(clientId)

          return null
        }

        setUploadStage(clientId, 'presign')

        const { url: putUrl, key } = await presignPhoto(
          {
            type: 'post_image',
            contentType: file.type,
            size: file.size
          },
          signal
        )

        throwIfUploadAborted(clientId)
        setUploadStage(clientId, 'upload')

        await uploadFileToS3(
          putUrl,
          file,
          (loaded, total, cancelCb) => {
            const currentOp = ensureUploadOp(clientId)
            currentOp.cancelCurrent = cancelCb

            if (isUploadAborted(clientId)) {
              try {
                cancelCb?.()
              } catch {}

              return
            }

            const list = [...(progressInfosRef.current?.val ?? [])]
            const index = list.findIndex(item => item.id === clientId)

            if (index < 0) return

            const item = list[index]

            item.percentage = Math.round(
              (loaded * 100) /
              (total || file.size || 1)
            )
            item.cancel = cancelCb
            item.error = ''
            item.type = 'photo'
            item.processing = true
            item.uploading = true

            syncProgress(list)
          },
          signal
        )

        throwIfUploadAborted(clientId)
        setUploadStage(clientId, 'attach')

        const attach = await attachPhotoFromS3(
          {
            s3_key: key,
            type: 'post',
            hash_image: fileHash
          },
          signal
        )

        throwIfUploadAborted(clientId)

        const resData = attach?.data?.data ?? attach?.data ?? attach

        safeSyncById(clientId, entry => {
          entry.photo_id = resData.id
          entry.status = resData.status ?? 'uploaded'
          entry.locked = !!priceRef.current
          entry.exif_data = resData.exif_data
          entry.hash_image = fileHash
          entry.exif_media = resData.exif_media
          entry.orientation = resData.original_orientation ?? entry.orientation
          entry.processing = false
          entry.uploading = false
          entry.serverReady = entry.status === 'finished'
          entry.percentage = 100
          entry.error = ''
        })

        setUploadStage(clientId, 'done')
        clearUploadOp(clientId)

        return attach
      }

      if (file.type.includes('video')) {
        let duration = 0
        let orientation: 'Landscape' | 'Portrait' | undefined

        try {
          const meta = await import('helpers/video/readClientMeta').then(
            async module => await module.readClientVideoMeta(file)
          )

          duration = meta.duration || 0
          orientation = meta.orientation
        } catch {}

        throwIfUploadAborted(clientId)

        setUploadStage(clientId, 'hash')

        const fileHash = await fastFileHash(file)
        throwIfUploadAborted(clientId)

        setUploadStage(clientId, 'dupe')

        const group_ids = extractGroupIds(postAudienceRef.current)

        const dupe = await checkVideoDupe(
          {
            h: fileHash,
            size: file.size,
            group_ids
          },
          signal
        )

        throwIfUploadAborted(clientId)

        /*
         * --------------------------------------------------
         * DUPLICATE VIDEO
         * --------------------------------------------------
         *
         * Existing backend thumbs are our fallback.
         * We still try to generate local thumbs so the user
         * gets the same local editing/scrubbing experience.
         */
        if (dupe?.status === 'OK') {
          safeSyncById(clientId, entry => {
            applyDuplicateVideoData(
              entry,
              dupe.data,
              {
                duration,
                orientation,
                hash: fileHash
              }
            )

            // FETCH THUMBS/STORYBOARD FOR DUPE
            const duplicateVideoId =
              dupe.data?.video_id ??
              dupe.data?.id

            if (typeof duplicateVideoId === 'string') {
              void getVideoThumbs([duplicateVideoId])
                .then(response => {
                  const row =
                    response?.data?.data?.find(
                      (item: any) =>
                        item?.id === duplicateVideoId
                    )

                  if (!row?.storyboard) return

                  safeSyncById(clientId, entry => {
                    entry.storyboard = row.storyboard
                  })
                })
                .catch(error => {
                  console.warn(
                    '[NewPost] Failed to load duplicate video storyboard',
                    error
                  )
                })
            }
            entry.processing = true
            entry.uploading = false
          })

          try {
            const generated = await generateClientThumbs(
              file,
              duration
            )

            throwIfUploadAborted(clientId)

              const expectedThumbCount =
                duration > 0
                  ? getExpectedClientThumbCount(duration)
                  : generated.thumbs.length

              const valid =
                await validateClientThumbs(
                  generated,
                  expectedThumbCount
                )

            throwIfUploadAborted(clientId)

            console.log(
              '[CLIENT THUMBS] duplicate validation',
              {
                valid,
                thumbs: generated.thumbs.length,
                scrubFrames: generated.scrubFrames.length
              }
            )

            if (valid) {
              safeSyncById(clientId, entry => {
                revokeClientThumbAssets(entry)
                applyGeneratedClientThumbs(
                  entry,
                  generated
                )
              })
            } else {
              revokeClientThumbs(generated)
            }
          } catch (error) {
            console.warn(
              '[NewPost] Duplicate client thumbnail generation failed; using server thumbs',
              error
            )
          }

          throwIfUploadAborted(clientId)

          safeSyncById(clientId, entry => {
            const hasThumbs =
              Array.isArray(entry.thumbnails) &&
              entry.thumbnails.length > 0

            entry.processing = false
            entry.uploading = false
            entry.percentage = 100
            entry.error = ''

            if (
              entry.thumbs_from === 'server'
            ) {
              entry.thumbsDone = hasThumbs
              entry.thumbsQueryDone = hasThumbs
            }
          })

          setPreviewPost(
            dupe.data.post ?? null
          )

          setUploadStage(
            clientId,
            'done'
          )

          clearUploadOp(
            clientId
          )

          return null
        }

        /*
         * --------------------------------------------------
         * NEW VIDEO
         * --------------------------------------------------
         */

        const advancedPreview = true

        setUploadStage(
          clientId,
          'presign'
        )

        const multipart =
          await startVideoMultipart(
            {
              type: 'post_video',
              contentType: file.type,
              size: file.size
            },
            signal
          )

        throwIfUploadAborted(clientId)

        const {
          key,
          uploadId,
          partSize,
          totalParts,
          parts
        } = multipart

        const uploadController =
          new AbortController()

        let multipartAborted = false

        const cleanupMultipart =
          async () => {
            if (multipartAborted) return

            multipartAborted = true

            try {
              uploadController.abort()
            } catch {}

            try {
              await abortVideoMultipart({
                key,
                uploadId
              })
            } catch (error) {
              console.error(
                '[NewPost] Failed to abort multipart upload',
                error
              )
            }
          }

        const handleOuterAbort = () => {
          void cleanupMultipart()
        }

        if (signal.aborted) {
          await cleanupMultipart()
          throwIfUploadAborted(clientId)
        }

        signal.addEventListener(
          'abort',
          handleOuterAbort,
          {
            once: true
          }
        )

        try {
          /*
           * Start the S3 upload first.
           *
           * Client thumbnail generation happens in parallel
           * and therefore does not delay the upload start.
           */
          setUploadStage(
            clientId,
            'upload'
          )

          const uploadPromise =
            uploadVideoMultipart({
              file,
              key,
              uploadId,
              partSize,
              totalParts,
              parts,
              contentType: file.type,
              concurrency: 4,
              signal:
                uploadController.signal,

              setProgress:
                (
                  loaded,
                  total
                ) => {
                  const currentOp =
                    ensureUploadOp(
                      clientId
                    )

                  const cancelUpload =
                    () => {
                      void cleanupMultipart()
                    }

                  currentOp.cancelCurrent =
                    cancelUpload

                  if (
                    isUploadAborted(
                      clientId
                    )
                  ) {
                    void cleanupMultipart()
                    return
                  }

                  const list = [
                    ...(
                      progressInfosRef
                        .current?.val ??
                      []
                    )
                  ]

                  const index =
                    list.findIndex(
                      item =>
                        item.id ===
                        clientId
                    )

                  if (index < 0) {
                    return
                  }

                  const entry =
                    list[index]

                  entry.percentage =
                    Math.round(
                      (
                        loaded *
                        100
                      ) /
                      (
                        total ||
                        file.size ||
                        1
                      )
                    )

                  entry.cancel =
                    cancelUpload

                  entry.error = ''
                  entry.type = 'video'

                  entry.previewOn =
                    getPreviewStatus(
                      userData
                        .post_preview_type
                    )

                  entry.processing =
                    true

                  entry.uploading =
                    true

                  syncProgress(list)
                }
            })

          /*
           * Generate + validate client thumbs independently.
           *
           * Failure here does NOT fail the video upload.
           * It only means attachVideoFromS3 must return
           * the backend-generated thumbnail set.
           */
          const clientThumbsPromise =
            generateClientThumbs(
              file,
              duration
            )
              .then(
                async generated => {
                  if (
                    isUploadAborted(
                      clientId
                    )
                  ) {
                    revokeClientThumbs(
                      generated
                    )

                    return {
                      generated:
                        null as ClientThumbs | null,
                      valid: false,
                      error: null as unknown
                    }
                  }

              const expectedThumbCount =
                duration > 0
                  ? getExpectedClientThumbCount(duration)
                  : generated.thumbs.length

              const valid =
                await validateClientThumbs(
                  generated,
                  expectedThumbCount
                )


                  if (
                    isUploadAborted(
                      clientId
                    )
                  ) {
                    revokeClientThumbs(
                      generated
                    )

                    return {
                      generated:
                        null as ClientThumbs | null,
                      valid: false,
                      error:
                        null as unknown
                    }
                  }

                  console.log(
                    '[CLIENT THUMBS] validation',
                    {
                      valid,
                      expectedThumbCount,
                      thumbs:
                        generated
                          .thumbs
                          .length,
                      scrubFrames:
                        generated
                          .scrubFrames
                          .length
                    }
                  )

                  if (!valid) {
                    revokeClientThumbs(
                      generated
                    )

                    return {
                      generated:
                        null as ClientThumbs | null,
                      valid: false,
                      error:
                        null as unknown
                    }
                  }

                  /*
                   * Show client thumbs immediately.
                   *
                   * Keep processing=true because the actual
                   * video upload/attach may still be running.
                   */
                  safeSyncById(
                    clientId,
                    entry => {
                      revokeClientThumbAssets(
                        entry
                      )

                      applyGeneratedClientThumbs(
                        entry,
                        generated
                      )

                      entry.processing =
                        true
                    }
                  )

                  return {
                    generated,
                    valid: true,
                    error:
                      null as unknown
                  }
                }
              )
              .catch(error => {
                console.error(
                  '[NewPost] Client thumbnail generation failed',
                  error
                )

                return {
                  generated:
                    null as ClientThumbs | null,
                  valid: false,
                  error
                }
              })

          /*
           * Wait for S3 upload.
           */
          const completedParts =
            await uploadPromise

          throwIfUploadAborted(
            clientId
          )

          if (
            !completedParts?.length
          ) {
            throw new Error(
              'Multipart video upload did not complete.'
            )
          }

          await completeVideoMultipart(
            {
              key,
              uploadId,
              parts:
                completedParts
            },
            signal
          )

          throwIfUploadAborted(
            clientId
          )

          /*
           * We need the client-thumb result before attach
           * because the backend needs client_thumbs_ready.
           */
          const clientThumbs =
            await clientThumbsPromise

          throwIfUploadAborted(
            clientId
          )

          const clientThumbsReady =
            clientThumbs.valid &&
            clientThumbs.generated !==
              null

          setUploadStage(
            clientId,
            'attach'
          )

          /*
           * If client_thumbs_ready=false, backend generates /
           * waits for its thumbs and returns them directly
           * in this response.
           */
          const attach =
            await attachVideoFromS3(
              {
                s3_key: key,

                duration:
                  duration > 0
                    ? Number(
                        duration.toFixed(
                          2
                        )
                      )
                    : null,

                orientation:
                  orientation ?? null,

                video_hash:
                  fileHash,

                preview:
                  getPreviewStatus(
                    userData
                      .post_preview_type
                  ),

                advanced_preview:
                  advancedPreview,

                client_thumbs_ready:
                  clientThumbsReady,

                type: 'post'
              } as any,
              signal
            )

          throwIfUploadAborted(
            clientId
          )

          const resData =
            attach?.data?.data ??
            attach?.data ??
            attach

          /*
           * Basic attached-video fields.
           */
          safeSyncById(
            clientId,
            entry => {
              entry.type = 'video'

              entry.storageUrl =
                resData?.path ??
                key

              entry.video_id =
                resData?.id ??
                entry.video_id ??
                null

              entry.duration =
                (
                  typeof resData
                    ?.duration ===
                  'number'
                    ? resData.duration
                    : duration
                ) ??
                entry.duration

              entry.orientation =
                resData?.orientation ??
                orientation ??
                entry.orientation

              entry.locked =
                !!priceRef.current

              entry.hash_video =
                fileHash

              entry.exif_video_media =
                resData
                  ?.exif_video_media ??
                null

              entry.uploading = false
              entry.percentage = 100
              entry.processing = true
              entry.error = ''
            }
          )

          throwIfUploadAborted(
            clientId
          )

          /*
           * --------------------------------------------------
           * CLIENT THUMBS WON
           * --------------------------------------------------
           */
          if (
            clientThumbsReady &&
            clientThumbs.generated
          ) {
            safeSyncById(
              clientId,
              entry => {
                /*
                 * They were probably already applied while
                 * upload was running, but apply again against
                 * the final attached entry for consistency.
                 */
                applyGeneratedClientThumbs(
                  entry,
                  clientThumbs.generated as ClientThumbs
                )

                entry.processing =
                  false

                entry.uploading =
                  false

                entry.percentage =
                  100

                entry.error = ''
              }
            )
          } else {
            /*
             * --------------------------------------------------
             * SERVER THUMBS WON
             * --------------------------------------------------
             *
             * Backend guarantees these are returned whenever
             * client_thumbs_ready=false.
             */
            const serverThumbnails =
              Array.isArray(
                resData?.thumbnails
              )
                ? resData.thumbnails.filter(
                    (
                      url: unknown
                    ): url is string =>
                      typeof url ===
                        'string' &&
                      url.length > 0
                  )
                : []

            const serverThumbnailTimes =
              Array.isArray(
                resData
                  ?.thumbnail_times
              )
                ? resData.thumbnail_times
                : Array.isArray(
                      resData
                        ?.thumbnailTimes
                    )
                  ? resData.thumbnailTimes
                  : []

            if (
              !serverThumbnails.length
            ) {
              throw new Error(
                'Backend did not return video thumbnails after client thumbnail generation failed.'
              )
            }

            const requestedActiveIndex =
              typeof resData
                ?.active_thumbnail ===
              'number'
                ? resData
                    .active_thumbnail
                : 0

            const activeIndex =
              requestedActiveIndex >=
                0 &&
              requestedActiveIndex <
                serverThumbnails.length
                ? requestedActiveIndex
                : 0

            safeSyncById(
              clientId,
              entry => {
                /*
                 * There should not normally be valid client
                 * blobs here, but clean any stale ones.
                 */
                revokeClientThumbAssets(
                  entry
                )

                entry.thumbnails =
                  serverThumbnails

                entry.thumbnailTimes =
                  serverThumbnailTimes

                entry.scrubFrames = []

                entry.thumbTarget =
                  serverThumbnails.length

                entry.thumbsDone =
                  true

                entry.thumbsQueryDone =
                  true

                entry.thumbs_from =
                  'server'

                entry.activeThumbnailIndex =
                  activeIndex

                entry.imagePreview =
                  resData
                    ?.active_thumb ??
                  serverThumbnails[
                    activeIndex
                  ] ??
                  serverThumbnails[0] ??
                  entry.imagePreview ??
                  null

                entry.processing =
                  false

                entry.uploading =
                  false

                entry.percentage =
                  100

                entry.error = ''
              }
            )
          }

          setUploadStage(
            clientId,
            'done'
          )

          clearUploadOp(
            clientId
          )

          return attach
        } catch (error) {
          await cleanupMultipart()
          throw error
        } finally {
          signal.removeEventListener(
            'abort',
            handleOuterAbort
          )
        }
      }

      if (
        file.type.includes(
          'audio'
        )
      ) {
        setUploadStage(
          clientId,
          'upload'
        )

        data.append(
          'audio',
          file
        )

        const res =
          await attachAudio(
            data,
            (
              loaded: number,
              total: number,
              cancelCb: () => void
            ) => {
              const currentOp =
                ensureUploadOp(
                  clientId
                )

              currentOp.cancelCurrent =
                cancelCb

              if (
                isUploadAborted(
                  clientId
                )
              ) {
                try {
                  cancelCb?.()
                } catch {}

                return
              }

              currentProgress = [
                ...(
                  progressInfosRef
                    .current?.val ??
                  []
                )
              ]

              const index =
                currentProgress.findIndex(
                  item =>
                    item.id ===
                    clientId
                )

              if (index >= 0) {
                currentProgress[
                  index
                ].percentage =
                  Math.round(
                    (
                      100 *
                      loaded
                    ) /
                    (
                      total ||
                      1
                    )
                  )

                currentProgress[
                  index
                ].cancel =
                  cancelCb

                currentProgress[
                  index
                ].error = ''

                currentProgress[
                  index
                ].type =
                  'audio'
              }

              syncProgress(
                currentProgress
              )
            },
            signal
          )

        throwIfUploadAborted(
          clientId
        )

        if (
          res?.data?.data?.path
        ) {
          const resData =
            res.data.data

          currentProgress = [
            ...(
              progressInfosRef
                .current?.val ??
              []
            )
          ]

          const index =
            currentProgress.findIndex(
              item =>
                item.id ===
                clientId
            )

          if (
            index >= 0 &&
            !isUploadAborted(
              clientId
            )
          ) {
            currentProgress[
              index
            ].previewUrl =
              resData.path

            currentProgress[
              index
            ].storageUrl =
              resData.storage_path

            currentProgress[
              index
            ].locked =
              !!priceRef.current

            syncProgress(
              currentProgress
            )
          }
        }

        setUploadStage(
          clientId,
          'done'
        )

        clearUploadOp(
          clientId
        )

        return res
      }

      clearUploadOp(
        clientId
      )

      return null
    } catch (error: any) {
      if (
        error?.code ===
          '__UPLOAD_ABORTED__' ||
        error?.message ===
          '__UPLOAD_ABORTED__' ||
        error?.name ===
          'AbortError' ||
        error?.code ===
          'ERR_CANCELED'
      ) {
        clearUploadOp(
          clientId
        )

        return null
      }

      const list = [
        ...(
          progressInfosRef
            .current?.val ??
          []
        )
      ]

      const index =
        list.findIndex(
          item =>
            item.id ===
            clientId
        )

      if (index >= 0) {
        list[index].error =
          error?.isAxiosError
            ? t(
                'errorUploadingFile'
              )
            : error?.message ??
              t(
                'errorUploadingFile'
              )

        list[index].processing =
          false

        list[index].uploading =
          false
      }

      syncProgress(list)

      clearUploadOp(
        clientId
      )

      throw new Error(
        error?.isAxiosError
          ? t(
              'errorUploadingFile'
            )
          : error?.message ??
            t(
              'errorUploadingFile'
            )
      )
    }
  },
  [
    t,
    userData.post_preview_type
  ]
)

  const uploadFiles = (files: File[]) => {
    const placeholders = files.map(file => {
      const clientId = cid()
      ensureUploadOp(clientId)

      return {
        id: clientId,
        client_id: clientId,
        percentage: 0,
        fileName: file.name,
        size: file.size,
        previewOn: true,
        type: file.type.startsWith('video/')
          ? 'video'
          : file.type.startsWith('image/')
            ? 'photo'
            : file.type.startsWith('audio/')
              ? 'audio'
              : 'other',
        processing: true,
        thumbsDone: file.type.startsWith('video/') ? false : undefined,
        thumbsQueryDone: file.type.startsWith('video/') ? false : undefined,
        previewUrl: URL.createObjectURL(file),
        error: '',
        locked: !!priceRef.current
      } as any
    })

    syncProgress([...(progressInfosRef.current?.val ?? []), ...placeholders])

    files.forEach((file, index) => {
      void upload(file, placeholders[index].id)
    })
  }

  const handleEditVideoPreview = (
    videoId: string,
    defaultPreview: string,
    storageSrc: string,
    duration?: number,
    orientation?: string,
    thumbnails?: string[],
    thumbnailTimes?: number[],
    imagePreview?: string,
    trimPreview?: { start: number; end: number },
    thumbType: 'clip' | 'thumb' = 'thumb',
    customThumbnailUrl?: string | null,
    customThumbnailTime?: number | null,
    customPreviewUrl?: string | null,
    previewVideoId?: string | null,
    activeThumbnailIndex?: number | null,
    thumbsQueryDone = true,
    scrubFrames?: ClientFrame[],
    previewThumbnailIndex?: number | null,
    customPreviewThumbKey?: string | null,
    customPreviewThumbnailUrl?: string | null,
    storyboard?: VideoPreviewData['storyboard'],
    cloudfront?: CloudFrontCredentials | null,
    customPreviewCloudfront?: CloudFrontCredentials | null
  ) => {
    const normalizedCustomThumbnailTime =
      customThumbnailTime != null && Number.isFinite(Number(customThumbnailTime))
        ? Number(customThumbnailTime)
        : null

    const hasCustomThumbnail =
      typeof customThumbnailUrl === 'string' &&
      customThumbnailUrl.trim() !== '' &&
      normalizedCustomThumbnailTime !== null

    let thumbnailIndex: number | undefined

    if (
      !hasCustomThumbnail &&
      typeof activeThumbnailIndex === 'number' &&
      thumbnails?.length
    ) {
      thumbnailIndex = activeThumbnailIndex
    } else if (!hasCustomThumbnail && imagePreview && thumbnails?.length) {
      const index = thumbnails.indexOf(imagePreview)
      if (index >= 0) thumbnailIndex = index
    }

    setVideoPreviewData({
      videoId,
      defaultPreview,
      storageSrc,
      duration,
      orientation,
      thumbnails,
      thumbnailTimes,
      cloudfront,
      customPreviewCloudfront,
      trimPreview,
      thumbnailIndex,
      thumbType,
      thumbsQueryDone,
      customThumbnailUrl: hasCustomThumbnail ? customThumbnailUrl : null,
      customThumbnailTime: hasCustomThumbnail ? normalizedCustomThumbnailTime : null,
      scrubFrames,
      customPreviewUrl: customPreviewUrl ?? null,
      previewVideoId: previewVideoId ?? null,
      previewThumbnailIndex: previewThumbnailIndex ?? null,
      customPreviewThumbKey: customPreviewThumbKey ?? null,
      customPreviewThumbnailUrl: customPreviewThumbnailUrl ?? null,
      storyboard,
    })

    setEditPreviewModalOpen(true)
  }

  const handleUpdatePreview = (data: ConfirmPayload) => {
    const currentList = (progressInfosRef.current?.val ?? progressInfos.val) as IProgressInfo[]

    const nextList = produce(currentList, draft => {
      const item = draft.find(info => info.previewUrl === data.defaultPrev) as any
      if (!item) return

      if (data.activeThumbnail) item.imagePreview = data.activeThumbnail

      item.thumbType = data.thumbType

      const selectedCustomThumbnail = typeof data.selectedTime === 'number'
      const selectedGeneratedThumbnail = typeof data.selectedIndex === 'number'
      const selectedUploadedThumbnail =
        typeof data.customThumbKey === 'string' && data.customThumbKey.length > 0

      if (selectedUploadedThumbnail) {
        item.custom_thumb_key = data.customThumbKey
        item.custom_thumb_crop = data.customThumbCrop ?? null
        item.custom_thumbnail = null
        item.customThumbnailUrl = null
        item.activeThumbnailIndex = null
      } else if (selectedCustomThumbnail) {
        item.custom_thumbnail = Number(data.selectedTime)
        item.customThumbnailUrl = data.activeThumbnail ?? null
        item.custom_thumb_key = null
        item.custom_thumb_crop = null
        item.activeThumbnailIndex = null
      } else if (selectedGeneratedThumbnail) {
        item.activeThumbnailIndex = Number(data.selectedIndex)
        item.custom_thumbnail = null
        item.customThumbnailUrl = null
        item.custom_thumb_key = null
        item.custom_thumb_crop = null
      } else if (data.activeThumbnail && Array.isArray(item.thumbnails)) {
        const derivedIndex = item.thumbnails.indexOf(data.activeThumbnail)

        if (derivedIndex >= 0) {
          item.activeThumbnailIndex = derivedIndex
          item.custom_thumbnail = null
          item.customThumbnailUrl = null
          item.custom_thumb_key = null
          item.custom_thumb_crop = null
        }
      }
      if (data.customPreviewThumbKey) {
        item.previewThumbIndex = null
        item.previewThumbKey = data.customPreviewThumbKey
        item.previewThumbUrl =
          data.customPreviewThumbnailUrl ?? null
      } else if (
        typeof data.previewThumbnailIndex === 'number'
      ) {
        item.previewThumbIndex =
          data.previewThumbnailIndex

        item.previewThumbKey = null
        item.previewThumbUrl =
          item.thumbnails?.[data.previewThumbnailIndex] ?? null
      } else {
        item.previewThumbIndex = null
        item.previewThumbKey = null
        item.previewThumbUrl = null
      }
      item.preview_video_id = data.previewVideoId ?? null
      item.customPreviewUrl = data.customPreviewUrl ?? null
      item.trimPreview =
        !data.previewVideoId &&
        data.thumbType === 'clip' &&
        data.clip
          ? {
              start: data.clip.start,
              end: data.clip.end
            }
          : undefined
    })

    syncProgress(nextList)
    setEditPreviewModalOpen(false)
  }

  const handleToggleMediaLocked = (idx: number) => {
    const updatedProgressInfos = [...progressInfos.val]
    const updatedSelectedFiles = [...selectedFiles]
    const currentItem = updatedProgressInfos[idx]
    const isCurrentlyLocked = !!currentItem.locked

    if (post.price && isCurrentlyLocked) {
      const lockedCount = updatedProgressInfos.filter(item => item.locked).length

      if (lockedCount === 1) {
        addToast('error', 'At least one attachment must remain locked.')
        return
      }
    }

    updatedProgressInfos[idx] = {
      ...currentItem,
      locked: !isCurrentlyLocked
    }

    const isNowUnlocked = isCurrentlyLocked

    if (isNowUnlocked && idx > 0) {
      const everyOtherItemLocked = updatedProgressInfos.every((item, index) =>
        index === idx ? !item.locked : !!item.locked
      )

      if (everyOtherItemLocked) {
        const [movedInfo] = updatedProgressInfos.splice(idx, 1)
        const [movedFile] = updatedSelectedFiles.splice(idx, 1)

        updatedProgressInfos.unshift(movedInfo)
        updatedSelectedFiles.unshift(movedFile)
      }
    }

    setProgressInfos({ val: updatedProgressInfos })
    setSelectedFiles(updatedSelectedFiles)
    progressInfosRef.current = { val: updatedProgressInfos }
  }

  const handleTogglePreviewOn = (idx: number) => {
    const current = [...(progressInfosRef.current?.val ?? progressInfos.val)]

    syncProgress(
      current.map((info, index) =>
        index === idx ? { ...info, previewOn: !info.previewOn } : info
      )
    )
  }

  const handlePollSubmit = async (pollData: IPoll) => {
    try {
      const bgUploadedSrc = (pollData as any).pollBgUploadedSrc as
        | string
        | null
        | undefined

      const response = await uploadPoll({
        type: pollData.type,
        question: pollData.question,
        groups: extractGroupIds(postAudience),
        answers: pollData.answers,
        background_color: bgUploadedSrc ? null : pollData.pollBg,
        background_src: bgUploadedSrc ?? null,
        schedule_date: post.schedule_date,
        schedule_timezone: post.schedule_date
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : null
      })

      const createdPost =
        response?.data?.data ??
        response?.data ??
        response

      if (createdPost?.id && !post.schedule_date) {
        await insertCreatedPost(createdPost)
      }

      navigate(`/${userData.username}`, {
        state: {
          skipProfileStoriesRefetch: true
        }
      })
    } catch (error) {
      console.error(error)
      addToast('error', t('error:errorSomethingWentWrong'))
    }
  }

  const handleGoalSubmit = async (goalData: IGoal) => {
    try {
      const bgUploadedSrc = (goalData as any).goalBgUploadedSrc as
        | string
        | null
        | undefined

      const response = await uploadGoal({
        text: goalData.title,
        goal: goalData.amount,
        groups: extractGroupIds(postAudience),
        background_color: bgUploadedSrc ? null : goalData.goalBg,
        background_src: bgUploadedSrc ?? null,
        schedule_date: post.schedule_date,
        schedule_timezone: post.schedule_date
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : null
      })

      const createdPost = response?.data?.data ?? response?.data ?? response

      if (createdPost?.id && !post.schedule_date) {
        await insertCreatedPost(createdPost)
      }

      navigate(`/${userData.username}`, {
        state: {
          skipProfileStoriesRefetch: true
        }
      })
    } catch (error) {
      console.error(error)
      addToast('error', t('error:errorSomethingWentWrong'))
    }
  }

  const preparePost = (progInfos: IProgressInfo[]) => {
    const photos = progInfos
      .filter(item => item.type === 'photo')
      .map(item => ({ item, photoId: item.photo_id }))
      .filter(
        (entry): entry is { item: IProgressInfo; photoId: string } =>
          typeof entry.photoId === 'string' && entry.photoId.length > 0
      )
      .map(({ item, photoId }) => ({
        photo_id: photoId,
        is_duplicate: !!(item as any).is_duplicate,
        hash_image: (item as any).hash_image ?? null,
        order: progInfos.indexOf(item) + 1,
        locked: !!item.locked,
        text: typeof item.text === 'object' ? item.text?.value ?? null : item.text ?? null
      }))

    const videos = progInfos
      .filter(item => item.type === 'video')
      .map(item => ({ item, videoId: item.video_id }))
      .filter(
        (entry): entry is { item: IProgressInfo; videoId: string } =>
          typeof entry.videoId === 'string' && entry.videoId.length > 0
      )
      .map(({ item, videoId }) => ({
        video_id: videoId,
        order: progInfos.indexOf(item) + 1,
        duration: typeof item.duration === 'number' ? item.duration : null,
        orientation: item.orientation ?? null,
        locked: !!item.locked,
        preview: !!(item as any).previewOn,
        active_thumbnail: (item as any).activeThumbnailIndex ?? null,
        preview_thumb: (item as any).previewThumbIndex ?? null,
        preview_thumb_key: (item as any).previewThumbKey ?? null,
        custom_thumbnail: (item as any).custom_thumbnail ?? null,
        custom_thumb_key: (item as any).custom_thumb_key ?? null,
        custom_thumb_crop: (item as any).custom_thumb_crop ?? null,
        preview_video_id: (item as any).preview_video_id ?? null,
        video_clip:
          item.previewOn &&
          !(item as any).preview_video_id &&
          item.trimPreview
            ? {
                start: item.trimPreview.start,
                end: item.trimPreview.end
              }
            : null,
        hash_video: (item as any).hash_video ?? null
      }))

    const sounds = progInfos
      .filter(item => item.type === 'audio' && item.storageUrl)
      .map(item => ({
        path: item.storageUrl,
        order: progInfos.indexOf(item) + 1,
        locked: item.locked
      }))

    return {
      body: post.body,
      photos,
      videos,
      sounds,
      price: post.price,
      groups: extractGroupIds(postAudience),
      categories: extractCategoryIds(selectedCategories),
      tags: post.tags,
      schedule_date: post.schedule_date,
      schedule_timezone: post.schedule_date
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null,
      shareOnTwitter: post.shareOnTwitter
    }
  }

  const getCreatedPostCountDelta = (prepared: ReturnType<typeof preparePost>) => {
    const photoCount = prepared.photos.length
    const videoCount = prepared.videos.length

    return {
      photo_count: photoCount,
      video_count: videoCount,
      premium_video_count: prepared.price ? videoCount : 0
    }
  }

  const profileFeedQueryKey = [
    'getFeed',
    userData.id,
    { type: 'all' }
  ] as const

  const insertCreatedPost = async (createdPost: any) => {
    if (!createdPost?.id) return

    await queryClient.cancelQueries(profileFeedQueryKey)

    insertPostIntoProfileQuery(
      queryClient,
      profileFeedQueryKey,
      {
        ...createdPost,
        processing: createdPost.encode_status === 'processing'
      }
    )
  }

const submitPost = async () => {
  if (userData?.status === 'restricted') {
    modalData.addModal(
      'Restricted Account',
      <ConfirmModal
        body="Please contact support for any assistance."
        cancelText="Cancel"
        confirmText="Submit Ticket"
        cancelButtonWidth="13"
        confirmButtonWidth="16"
        cancelFn={() => {
          modalData.clearModal()
        }}
        confirmFn={() => {
          modalData.clearModal()
          navigate('/support/home')
        }}
      />
    )

    return
  }

const hasMediaAttachments =
  progressInfos.val.some(item =>
    ['photo', 'video', 'audio', 'sound'].includes(item.type)
  ) ||
  !!pollData?.pollBgImg ||
  !!goalData?.goalBgImg ||
  !!goalData?.goalBgUploadedSrc

const hasPrice = Number(post.price ?? 0) > 0

const requiresPayoutMethod =
  userData?.role === 'model' &&
  !userData?.payout_method &&
  (hasMediaAttachments || hasPrice)

if (requiresPayoutMethod) {
  modalData.addModal(
    t('addPayoutMethod'),
    <AddPayoutMethodModal
      body="Add a Payout Method to Start Posting Today"
      confirmFn={() => {
        modalData.clearModal()
        navigate('/settings/general/payout-settings/payment-method')
      }}
    />
  )

  return
}
  setPostUploading(true)

  try {

      if (pollData) {
        await handlePollSubmit(pollData)
        return
      }

      if (goalData) {
        await handleGoalSubmit(goalData)
        return
      }

      const prepared = preparePost(progressInfos.val)

      if (
        prepared.price &&
        !prepared.body &&
        prepared.photos.length === 0 &&
        prepared.videos.length === 0
      ) {
        addToast('error', t('validation:premiumPostCannotBeAudioOnly'))
        return
      }

      const hasVideoPreview = prepared.videos.some(
        video =>
          video.preview &&
          (
            !!video.preview_video_id ||
            video.video_clip != null
          )
      )

      const hasVideoThumbnailPreview = prepared.videos.some(
        video =>
          video.preview &&
          !video.preview_video_id &&
          video.video_clip == null
      )

      const resolvedPostPreviewType = hasVideoPreview
        ? 'video'
        : hasVideoThumbnailPreview
          ? 'picture'
          : userData?.post_preview_type ?? null

      const response = await uploadPost({
        ...prepared,
        post_preview_type: resolvedPostPreviewType,
        tagged_users: selectedTaggedCreators.map(user => ({
          id: user.id,
          public_tag: user.public_tag
        }))
      })

      const createdPost = response?.data?.data ?? response?.data ?? response

      if (createdPost?.id && !prepared.schedule_date) {
        await insertCreatedPost(createdPost)
      }

      const countDelta = getCreatedPostCountDelta(prepared)

      incrementProfileMediaCounts(
        userData.username,
        {
          photo_count: countDelta.photo_count,
          video_count: countDelta.video_count,
          premium_video_count: countDelta.premium_video_count
        },
        userData.id
      )

      queryClient.setQueryData(['profile', userData.username], (old: any) => {
        if (!old) return old

        return {
          ...old,
          photo_count: Number(old.photo_count ?? 0) + countDelta.photo_count,
          video_count: Number(old.video_count ?? 0) + countDelta.video_count,
          premium_video_count:
            Number(old.premium_video_count ?? 0) + countDelta.premium_video_count
        }
      })

      navigate(`/${userData.username}`, {
        state: {
          skipProfileStoriesRefetch: true
        }
      })
    } catch (error) {
      console.error(error)
      addToast('error', t('error:errorSomethingWentWrong'))
    } finally {
      setPostUploading(false)
    }
  }

  const renderContent = () => {
    if (pollData) {
      return (
        <PollPreview
          pollData={pollData}
          clearPollData={() => {
            setPostType('')
            setPollData(null)
          }}
        />
      )
    }

    if (goalData) {
      return (
        <GoalPreview
          goalData={goalData}
          clearGoalData={() => {
            setPostType('')
            setGoalData(null)
          }}
        />
      )
    }

    return (
      <PostText
        body={post.body}
        tags={post.tags}
        setText={(body: string, tags: ITag[]) => {
          setPost(prev => ({ ...prev, body, tags }))
        }}
      />
    )
  }

  if (addPollOpen) {
    const portal = document.getElementById('portal')
    if (!portal) return null

    if (!portal.classList.contains('portal--open')) portal.classList.add('portal--open')

    return createPortal(
      <AddPoll
        handleClose={() => {
          portal.classList.remove('portal--open')
          setAddPollOpen(false)
        }}
        handleSetPollData={(data: IPoll) => {
          setPostType('poll')
          portal.classList.remove('portal--open')
          setPollData(data)
        }}
        initialBackgroundImage={firstPhotoSeed?.previewUrl ?? null}
        initialBackgroundFile={firstPhotoSeed?.file ?? null}
        initialBackgroundUploadedSrc={firstPhotoSeed?.storageUrl ?? null}
        initialBackgroundOrientation={firstPhotoSeed?.orientation ?? ''}
      />,
      portal
    )
  }

  if (addGoalOpen) {
    const portal = document.getElementById('portal')
    if (!portal) return null

    if (!portal.classList.contains('portal--open')) portal.classList.add('portal--open')

    return createPortal(
      <AddGoal
        handleClose={() => {
          portal.classList.remove('portal--open')
          setAddGoalOpen(false)
        }}
        handleSetGoalData={(data: IGoal) => {
          setPostType('goal')
          setGoalData(data)
          portal.classList.remove('portal--open')
          setAddGoalOpen(false)
        }}
        initialBackgroundImage={firstPhotoSeed?.previewUrl ?? null}
        initialBackgroundFile={firstPhotoSeed?.file ?? null}
        initialBackgroundUploadedSrc={firstPhotoSeed?.storageUrl ?? null}
        initialBackgroundOrientation={firstPhotoSeed?.orientation ?? ''}
      />,
      portal
    )
  }

  return (
    <>
      {editPreviewModalOpen && videoPreviewData && (
      <EditPreviewModal
          defaultPreview={videoPreviewData.defaultPreview}
          cloudfront={videoPreviewData.cloudfront}
          customPreviewCloudfront={videoPreviewData.customPreviewCloudfront}
          storageSrc={videoPreviewData.storageSrc}
          duration={videoPreviewData.duration}
          thumbnails={videoPreviewData.thumbnails}
          thumbnailTimes={videoPreviewData.thumbnailTimes}
          uploadCustomThumbnail={uploadCustomVideoThumbnail}
          uploadCustomPreviewThumbnail={async file =>
            await uploadCustomPreviewThumbnail(
              file,
              videoPreviewData.videoId
            )
          }
          thumbsQueryDone={videoPreviewData.thumbsQueryDone}
          thumbnailIndex={videoPreviewData.thumbnailIndex}
          previewThumbnailIndex={videoPreviewData.previewThumbnailIndex}
          customPreviewThumbKey={
            videoPreviewData.customPreviewThumbKey
          }
          customPreviewThumbnailUrl={
            videoPreviewData.customPreviewThumbnailUrl
          }
          trimPreview={videoPreviewData.trimPreview}
          orientation={videoPreviewData.orientation}
          initialPreviewOn={
            videoPreviewData.thumbType
              ? videoPreviewData.thumbType === 'clip'
              : userData?.post_preview_type === 'video'
          }
          cancelCb={() => setEditPreviewModalOpen(false)}
          thumbType={videoPreviewData.thumbType}
          confirmCb={handleUpdatePreview}
          customThumbnailUrl={videoPreviewData.customThumbnailUrl}
          customThumbnailTime={videoPreviewData.customThumbnailTime}
          scrubFrames={videoPreviewData.scrubFrames}
          customPreviewUrl={videoPreviewData.customPreviewUrl}
          previewVideoId={videoPreviewData.previewVideoId}
          uploadCustomPreview={uploadCustomVideoPreview}
          storyboard={videoPreviewData.storyboard}
        />
      )}

      {recorderType === 'video' && (
        <MediaRecorder
          type={recorderType}
          status={status}
          handleClose={() => setRecorderType('')}
          setRecordedChunks={setRecordedChunks}
          handleAudioStart={startRecording}
          handleAudioStop={stopRecording}
        />
      )}

      {recorderType === 'audio' && (
        <AudioRecorder
          type={recorderType}
          status={audioRecording}
          handleClose={() => setRecorderType('')}
          handleAudioStart={startAudioRecording}
          handleAudioStop={stopAudioRecording}
        />
      )}

      <NewPostContainer
        className={`newpost ${pollData ?? goalData ? 'newpost--pollPreview' : ''}`}
        $editPreviewModalOpen={editPreviewModalOpen}
      >
        <PostVisibility
          isEdit={false}
          audience={postAudience}
          groups={groups}
          setAudience={(value: IGroup[]) => {
            setPostAudience(value)
            postAudienceRef.current = value
            void rerunDupeChecks(value)
          }}
        />

        <div className="newpost__body">
          {renderContent()}

          <div className="newpost__body__bottom">
            {progressInfos.val.map(
              (item: IProgressInfo, key: number) =>
                (item.type === 'sound' || item.type === 'audio') && (
                  <AudioMessage
                    key={key}
                    customClass={`newpost__audiomessage ${
                      audioOnly ? '' : 'newpost__audiomessage--active'
                    }`}
                    audioBlob={item.previewUrl}
                    audioReady={!!item.previewUrl}
                    waveColor="gray"
                    waveBackgroundColor="transparent"
                    processing={item.percentage > 0 && item.percentage < 100}
                    percentage={item.percentage}
                    uploadError={item.error}
                    hasDeleteBtn
                    deleteFn={() => removeFile(key)}
                  />
                )
            )}

            {selectedTaggedCreators.length > 0 && (
              <div className="newpost__selectedTagsRow">
                <button
                  type="button"
                  className="newpost__tagCreatorsButton"
                  onClick={() => setTagCreatorsOpen(prev => !prev)}
                >
                  <div className="newpost__tagCreatorsButton__avatars">
                    {selectedTaggedCreators.slice(0, 3).map((creator, index) => (
                      <div
                        key={creator.id}
                        className="newpost__tagCreatorsButton__avatar"
                        style={{
                          marginLeft: index === 0 ? 0 : index === 1 ? '-0.75rem' : '-0.5rem',
                          zIndex: index + 1
                        }}
                      >
                        <img
                          src={creator.avatarUrl}
                          alt={creator.display}
                          className={
                            creator.public_tag
                              ? ''
                              : 'newpost__tagCreatorsButton__avatar--private'
                          }
                        />
                      </div>
                    ))}
                  </div>

                  <span>
                    {selectedTaggedCreators.length === 1
                      ? selectedTaggedCreators[0].display
                      : `${selectedTaggedCreators.length} Tags`}
                  </span>
                </button>
              </div>
            )}

            <PostMediaInputs
              filesAdded={
                (progressInfos.val.length > 0 && !audioOnly) || !!pollData || !!goalData
              }
              handleSetUpload={selectFiles}
              appendAttachments={appendAttachments}
              handleAddPollOpen={() => setAddPollOpen(true)}
              handleAddGoalOpen={() => setAddGoalOpen(true)}
              postType={postType}
              handleOpenRecorder={(type: string) => setRecorderType(type)}
              hasAudio={hasAudio}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              setProgressInfos={setProgressInfos}
              progressInfos={progressInfos}
              hasOnlyPhotos={hasOnlyPhotos}
              hasBlockingMediaForPollGoal={hasBlockingMediaForPollGoal}
              selectedTaggedCreators={selectedTaggedCreators}
              setSelectedTaggedCreators={setSelectedTaggedCreators}
              tagCreatorsOpen={tagCreatorsOpen}
              setTagCreatorsOpen={setTagCreatorsOpen}
            />

            {!pollData && !goalData && (
              <div
                className={`newpost__body__media ${
                  progressInfos.val.length > 0 && !audioOnly
                    ? 'newpost__body__media--active'
                    : ''
                }`}
              >
                <div className="newpost__body__media__wrapper" ref={dropRef}>
                  {progressInfos.val.map((item: IProgressInfo, key: number) => {
                    const id = getId(item)

                    if (item.type === 'photo') {
                      return (
                        <div key={id} data-id={id} className="drop-card drop-card--image">
                          <PostImage
                            setPreviewOpen={setPreviewOpen}
                            setPreviewMedia={setPreviewMedia}
                            mediaDta={item.exif_media}
                            isEdit
                            id={key}
                            imagePreview={item.imagePreview}
                            processing={!!item.processing}
                            attachmentCount={attachmentCount}
                            percentage={item.percentage ?? 0}
                            abortUploadCb={() => removeFile(key)}
                            uploadError={item.error}
                            previewUrl={item.previewUrl}
                            locked={item.locked}
                            isPremium={!!post.price}
                            removeFile={() => removeFile(key)}
                            removeByUrl={() => removeFile(key)}
                            editPreview={() => {
                              modalContext.addModal(
                                '',
                                <PhotoEditView
                                  photo={{ url: item.previewUrl, text: item.text }}
                                  handleClose={() => modalContext.clearModal()}
                                  handleSetPhotoData={data => {
                                    setProgressInfos(
                                      produce(progressInfos, draft => {
                                        draft.val[key].text = data.text
                                      })
                                    )
                                  }}
                                />,
                                true,
                                true,
                                'fullScreen no-close'
                              )
                            }}
                            handleToggleMediaLocked={handleToggleMediaLocked}
                          />

                          {item.exif_media?.entity_type === 'Post' &&
                            item.exif_media?.updated_at &&
                            item.previewUrl && (
                              <div className="exif_mediaDate">
                                <span className="exif_mediaDate__label">Posted</span>{' '}
                                <span>
                                  {new Date(item.exif_media.updated_at).toLocaleDateString(
                                    'en-US',
                                    {
                                      month: 'short',
                                      day: 'numeric'
                                    }
                                  )}
                                </span>
                              </div>
                            )}

                          {isMessageEntity(item.exif_media?.entity_type) && item.previewUrl && (
                            <div className="exif_mediaDate">
                              <PreviouslyMessaged width="15" height="10" color="#ffffff" />
                              <span>{item.exif_media?.viewed ?? 0}</span>
                            </div>
                          )}
                        </div>
                      )
                    }

                    if (item.type === 'video') {
                      return (
                        <div key={id} data-id={id} className="drop-card drop-card--video">
                          <PostVideo
                            setPreviewOpen={setPreviewOpen}
                            setPreviewMedia={setPreviewMedia}
                            mediaDta={item.exif_video_media}
                            isEdit
                            attachmentCount={attachmentCount}
                            id={key}
                            processing={!!item.processing}
                            percentage={item.percentage ?? 0}
                            thumbsReady={
                              item.fileName?.startsWith('shared-') ||
                              ((item as any).thumbsDone === true &&
                                (item as any).thumbsQueryDone === true)
                            }
                            abortUploadCb={() => removeFile(key)}
                            uploadError={item.error}
                            previewUrl={item.previewUrl}
                            imagePreview={item.imagePreview}
                            locked={item.locked}
                            isPremium={!!post.price}
                            removeFile={() => removeFile(key)}
                            removeByUrl={() => removeFile(key)}
                            editPreview={() => {
                              const resolvedThumbType: 'clip' | 'thumb' =
                                (item as any).thumbType === 'clip' ||
                                (item as any).thumbType === 'thumb'
                                  ? (item as any).thumbType
                                  : item.trimPreview
                                    ? 'clip'
                                    : 'thumb'

                              handleEditVideoPreview(
                                String(item.video_id),
                                item.previewUrl,
                                item.storageUrl,
                                item.duration,
                                item.orientation,
                                item.thumbnails,
                                (item as any).thumbnailTimes,
                                item.imagePreview,
                                item.trimPreview,
                                resolvedThumbType,
                                (item as any).customThumbnailUrl ?? null,
                                (item as any).custom_thumbnail ?? null,
                                (item as any).customPreviewUrl ?? null,
                                (item as any).preview_video_id ?? null,
                                (item as any).activeThumbnailIndex ?? null,
                                (item as any).thumbsQueryDone ?? true,
                                (item as any).scrubFrames ?? [],
                                (item as any).previewThumbIndex ?? null,
                                (item as any).previewThumbKey ?? null,
                                (item as any).previewThumbUrl ?? null,
                                item.storyboard ?? null,
                                (item as any).cloudfront ?? null,
                                (item as any).customPreviewCloudfront ?? null
                                )
                            }}
                            previewOn={item.previewOn}
                            trimmedVideo={item.trimPreview}
                            handleToggleMediaLocked={handleToggleMediaLocked}
                            handleTogglePreviewOn={handleTogglePreviewOn}
                          />

                            {item.exif_video_media?.entity_type === 'Post' &&
                              item.exif_video_media?.updated_at &&
                              item.previewUrl &&
                              !item.processing &&
                              !item.uploading &&
                              (item as any).thumbsDone === true &&
                              (item as any).thumbsQueryDone === true && (
                                <div className="exif_mediaDate">
                                  <span className="exif_mediaDate__label">
                                    Posted
                                  </span>{' '}
                                  <span>
                                    {new Date(
                                      item.exif_video_media.updated_at
                                    ).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </span>
                                </div>
                            )}
                          {isMessageEntity(item.exif_video_media?.entity_type) &&
                            item.previewUrl && (
                              <div className="exif_mediaDate">
                                <PreviouslyMessaged width="15" height="10" color="#ffffff" />
                                <span>{item.exif_video_media?.viewed ?? 0}</span>
                              </div>
                            )}
                        </div>
                      )
                    }

                    return null
                  })}

                  {progressInfos.val.length > 0 && !audioOnly && (
                    <div className="drop-card add-anything-card">
                      <AddAnything handleSetUpload={selectFiles} />
                    </div>
                  )}
                </div>
              </div>
            )}

            <PostInputs
              isUploading={postUploading}
              price={post.price}
              hasCategories={selectedCategories.length > 0}
              setPrice={(value: number) => {
                setPost(prev => ({ ...prev, price: value }))

                const current = (progressInfosRef.current?.val ??
                  progressInfos.val) as IProgressInfo[]

                syncProgress(
                  current.map(info => ({
                    ...info,
                    locked: !!value
                  }))
                )
              }}
              submitPost={submitPost}
              postReady={postReady || !!pollData || !!goalData}
              updateDate={(value: Date) => {
                const now = new Date()

                if (value < now) {
                  addToast('error', t('error:errorInvalidDate'))
                  return
                }

                setPost(prev => ({
                  ...prev,
                  schedule_date: value
                }))
              }}
              scheduleDate={post.schedule_date}
              removeSchedule={() =>
                setPost(prev => ({
                  ...prev,
                  schedule_date: null
                }))
              }
              addCategories={handleOpenCategories}
              userShareOnTwitter={userData?.auto_post_to_twitter as boolean}
              toggleShareOnTwitter={(share: boolean) =>
                setPost(prev => ({
                  ...prev,
                  shareOnTwitter: share
                }))
              }
            />
          </div>
        </div>

        {previewOpen && (
          <PostModal
            postId={previewPost?.id ?? previewMedia}
            post={previewPost}
            newPostPage
            closeFn={() => setPreviewOpen(false)}
          />
        )}
      </NewPostContainer>

      <DesktopAdditionalContent />
    </>
  )
}

interface NewPostContainerProps {
  $editPreviewModalOpen: boolean
}

const NewPostContainer = styled.div<NewPostContainerProps>``